import React, { useCallback, useEffect, useMemo, useState, memo, Component } from 'react';
import { Plus, Building2, Users, FileText, X, Edit2, Trash2, Phone, Mail, User, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { useOrders } from '../contexts/OrderContext';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
interface Branch {
  id: string;
  name: string;
  address: string;
  companyId: string;
  orderCutoffTime?: string;
  deliveryStartTime?: string;
  deliveryEndTime?: string;
}
interface Company {
  id: string;
  name: string;
  rnc: string;
  logo?: string;
  branches: Branch[];
  employees: number;
  taxExempt: boolean;
  subsidy: string;
  orderCutoffTime: string;
  deliveryStartTime: string;
  deliveryEndTime: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
}
export function CompanySettings() {
  const [searchTerm, setSearchTerm] = useState('');
  const [taxFilter, setTaxFilter] = useState('Todos');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [employeeCounts, setEmployeeCounts] = useState<Record<string, number>>({});
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const {
    orders
  } = useOrders();
  const {
    addLog
  } = useActivityLog();
  // Load companies and employee counts
  useEffect(() => {
    loadCompanies();
  }, []);
  const loadCompanies = async () => {
    setLoading(true);
    try {
      if (isSupabaseConfigured()) {
        // Load companies
        const companiesData = await supabaseClient.getCompanies();
        // Load employees to count them per company
        const employeesData = await supabaseClient.getEmployees();
        // Count employees per company
        const counts: Record<string, number> = {};
        employeesData.forEach((emp: any) => {
          const companyId = emp.company_id;
          counts[companyId] = (counts[companyId] || 0) + 1;
        });
        setEmployeeCounts(counts);
        setCompanies(companiesData);
      } else {
        // Fallback to localStorage
        const saved = localStorage.getItem('criket_companies');
        setCompanies(saved ? JSON.parse(saved) : []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      const saved = localStorage.getItem('criket_companies');
      setCompanies(saved ? JSON.parse(saved) : []);
    } finally {
      setLoading(false);
    }
  };
  const getOrderCountByCompany = (companyName: string) => {
    return orders.filter(o => o.company === companyName).length;
  };
  const getEmployeeCountByCompany = (companyId: string) => {
    return employeeCounts[companyId] || 0;
  };
  const filteredCompanies = useMemo(() => {
    return companies.filter(c => {
      const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.rnc.includes(searchTerm);
      const matchesTax = taxFilter === 'Todos' || taxFilter === 'Exento ITBIS' && c.taxExempt || taxFilter === 'No Exento' && !c.taxExempt;
      return matchesSearch && matchesTax;
    });
  }, [companies, searchTerm, taxFilter]);
  const handleDetails = useCallback((company: Company) => {
    setSelectedCompany(company);
    setShowDetailsModal(true);
  }, []);
  const handleEdit = useCallback((company: Company) => {
    setSelectedCompany(company);
    setShowEditModal(true);
  }, []);
  const handleAddCompany = useCallback(() => {
    setSelectedCompany(null);
    setShowAddModal(true);
  }, []);
  const saveNewCompany = useCallback(async (formData: any) => {
    try {
      if (isSupabaseConfigured()) {
        await supabaseClient.createCompany({
          name: formData.name,
          rnc: formData.rnc,
          logo: formData.logo,
          branches: formData.branches,
          taxExempt: formData.taxExempt,
          subsidy: formData.subsidy,
          orderCutoffTime: formData.orderCutoffTime,
          deliveryStartTime: formData.deliveryStartTime,
          deliveryEndTime: formData.deliveryEndTime,
          contactName: formData.contactName,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone
        });
      }
      await loadCompanies();
      addLog('Empresa Creada', `${formData.name} - RNC: ${formData.rnc}`, 'company');
      setShowAddModal(false);
    } catch (error) {
      console.error('Error creating company:', error);
      alert('Error al crear la empresa');
    }
  }, [addLog]);
  const saveEditedCompany = useCallback(async (formData: any) => {
    if (!selectedCompany) return;
    try {
      if (isSupabaseConfigured()) {
        await supabaseClient.updateCompany(selectedCompany.id, {
          name: formData.name,
          rnc: formData.rnc,
          logo: formData.logo,
          taxExempt: formData.taxExempt,
          subsidy: formData.subsidy,
          orderCutoffTime: formData.orderCutoffTime,
          deliveryStartTime: formData.deliveryStartTime,
          deliveryEndTime: formData.deliveryEndTime,
          branches: formData.branches,
          contactName: formData.contactName,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone
        });
      }
      await loadCompanies();
      addLog('Empresa Actualizada', `${formData.name} - ${formData.branches.length} sucursales`, 'company');
      setShowEditModal(false);
      setSelectedCompany(null);
    } catch (error) {
      console.error('Error updating company:', error);
      alert('Error al actualizar la empresa');
    }
  }, [selectedCompany, addLog]);
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-797">
        <div className="text-center" data-id="element-798">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" data-id="element-799" />
          <p className="text-gray-500" data-id="element-800">Cargando empresas...</p>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-id="element-801">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8" data-id="element-802">
        <div data-id="element-803">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-804">
            Gestión de Empresas
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-805">
            Administra las empresas clientes y sus configuraciones
          </p>
        </div>
        <Button leftIcon={<Plus size={18} data-id="element-807" />} onClick={handleAddCompany} data-id="element-806">
          Nueva Empresa
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-808">
        <SearchBar placeholder="Buscar por nombre o RNC..." className="w-full sm:flex-1" onSearch={setSearchTerm} data-id="element-809" />
        <FilterDropdown label="Estado Fiscal" value={taxFilter} options={['Todos', 'Exento ITBIS', 'No Exento']} onChange={setTaxFilter} className="w-full sm:w-48" data-id="element-810" />
      </div>

      {filteredCompanies.length === 0 ? <div className="text-center py-20 bg-white rounded-xl border border-gray-200" data-id="element-811">
          <Building2 size={48} className="mx-auto text-gray-300 mb-4" data-id="element-812" />
          <h3 className="text-lg font-medium text-gray-900" data-id="element-813">
            No se encontraron empresas
          </h3>
          <p className="text-gray-500 mt-2" data-id="element-814">
            {searchTerm || taxFilter !== 'Todos' ? 'Intenta con otros filtros' : 'Agrega tu primera empresa'}
          </p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-815">
          {filteredCompanies.map(company => {
        const orderCount = getOrderCountByCompany(company.name);
        const employeeCount = getEmployeeCountByCompany(company.id);
        return <CompanyCard key={company.id} company={company} orderCount={orderCount} employeeCount={employeeCount} onEdit={handleEdit} onDetails={handleDetails} data-id="element-816" />;
      })}
        </div>}

      {/* Modals */}
      {(showAddModal || showEditModal) && <CompanyFormModal company={selectedCompany} onClose={() => {
      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedCompany(null);
    }} onSave={showAddModal ? saveNewCompany : saveEditedCompany} data-id="element-817" />}

      {showDetailsModal && selectedCompany && <CompanyDetailsModal company={selectedCompany} orderCount={getOrderCountByCompany(selectedCompany.name)} employeeCount={getEmployeeCountByCompany(selectedCompany.id)} onClose={() => {
      setShowDetailsModal(false);
      setSelectedCompany(null);
    }} data-id="element-818" />}
    </div>;
}
// Company Card Component
function CompanyCard({
  company,
  orderCount,
  employeeCount,
  onEdit,
  onDetails
}: any) {
  const getBrandColor = (name: string) => {
    const colors = ['bg-blue-100 text-blue-800 border-blue-200', 'bg-purple-100 text-purple-800 border-purple-200', 'bg-green-100 text-green-800 border-green-200', 'bg-orange-100 text-orange-800 border-orange-200', 'bg-pink-100 text-pink-800 border-pink-200'];
    const index = name.length % colors.length;
    return colors[index];
  };
  const brandColorClass = getBrandColor(company.name);
  return <Card className="hover:shadow-lg transition-shadow flex flex-col h-full" data-id="element-819">
      <div className="flex justify-between items-start mb-4" data-id="element-820">
        <div className="flex items-center gap-3" data-id="element-821">
          {company.logo ? <img src={company.logo} alt={company.name} className="w-12 h-12 object-contain rounded-lg bg-gray-50 p-2" data-id="element-822" /> : <div className="p-3 bg-gray-100 rounded-lg" data-id="element-823">
              <Building2 className="w-6 h-6 text-gray-700" data-id="element-824" />
            </div>}
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${company.taxExempt ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`} data-id="element-825">
          {company.taxExempt ? 'Exento ITBIS' : 'ITBIS Aplica'}
        </span>
      </div>

      <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-826">{company.name}</h3>
      <p className="text-sm text-gray-500 mb-2 font-mono" data-id="element-827">{company.rnc}</p>

      <div className="mb-4 p-2 bg-orange-50 rounded-lg border border-orange-200" data-id="element-828">
        <p className="text-xs font-medium text-orange-700" data-id="element-829">
          📅 Cierre de pedidos:{' '}
          <span className="font-bold" data-id="element-830">{company.orderCutoffTime}</span>
        </p>
      </div>

      <div className="mb-4 flex-1" data-id="element-831">
        <p className="text-xs font-medium text-gray-500 mb-2" data-id="element-832">
          Sucursales ({company.branches?.length || 0})
        </p>
        <div className="flex flex-wrap gap-2" data-id="element-833">
          {company.branches?.slice(0, 5).map((branch: Branch) => <span key={branch.id} className={`px-2 py-1 text-xs font-medium rounded-full border ${brandColorClass}`} data-id="element-834">
              {branch.name}
            </span>)}
          {company.branches?.length > 5 && <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 border border-gray-200" data-id="element-835">
              +{company.branches.length - 5} más
            </span>}
        </div>
      </div>

      <div className="space-y-2 border-t border-gray-100 pt-4 mt-auto" data-id="element-836">
        <div className="flex justify-between text-sm" data-id="element-837">
          <span className="text-gray-500 flex items-center gap-2" data-id="element-838">
            <Users size={14} data-id="element-839" /> Empleados
          </span>
          <span className="font-medium text-gray-900" data-id="element-840">{employeeCount}</span>
        </div>
        <div className="flex justify-between text-sm" data-id="element-841">
          <span className="text-gray-500 flex items-center gap-2" data-id="element-842">
            <FileText size={14} data-id="element-843" /> Pedidos
          </span>
          <span className="font-bold text-gray-900" data-id="element-844">{orderCount}</span>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2" data-id="element-845">
        <Button variant="outline" size="sm" fullWidth leftIcon={<Edit2 size={14} data-id="element-847" />} onClick={() => onEdit(company)} data-id="element-846">
          Editar
        </Button>
        <Button variant="ghost" size="sm" fullWidth onClick={() => onDetails(company)} data-id="element-848">
          Detalles
        </Button>
      </div>
    </Card>;
}
// Company Form Modal Component
function CompanyFormModal({
  company,
  onClose,
  onSave
}: any) {
  const [formData, setFormData] = useState(() => ({
    name: company?.name || '',
    rnc: company?.rnc || '',
    logo: company?.logo || '',
    subsidy: company?.subsidy || 'RD$ 0',
    taxExempt: company?.taxExempt || false,
    orderCutoffTime: company?.orderCutoffTime || '23:00',
    deliveryStartTime: company?.deliveryStartTime || '12:00',
    deliveryEndTime: company?.deliveryEndTime || '14:00',
    contactName: company?.contactName || '',
    contactEmail: company?.contactEmail || '',
    contactPhone: company?.contactPhone || '',
    branches: company?.branches || []
  }));
  const [newBranch, setNewBranch] = useState({
    name: '',
    address: '',
    orderCutoffTime: '',
    deliveryStartTime: '',
    deliveryEndTime: ''
  });
  const [editingBranchIndex, setEditingBranchIndex] = useState<number | null>(null);
  const handleChange = useCallback((field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  }, [formData, onSave]);
  const addBranch = useCallback(() => {
    if (!newBranch.name || !newBranch.address) {
      alert('Por favor completa el nombre y dirección de la sucursal');
      return;
    }
    const branch: Branch = {
      id: `branch-${Date.now()}`,
      name: newBranch.name,
      address: newBranch.address,
      companyId: company?.id || '0',
      orderCutoffTime: newBranch.orderCutoffTime || formData.orderCutoffTime,
      deliveryStartTime: newBranch.deliveryStartTime || formData.deliveryStartTime,
      deliveryEndTime: newBranch.deliveryEndTime || formData.deliveryEndTime
    };
    if (editingBranchIndex !== null) {
      const updatedBranches = [...formData.branches];
      updatedBranches[editingBranchIndex] = {
        ...updatedBranches[editingBranchIndex],
        ...branch
      };
      setFormData(prev => ({
        ...prev,
        branches: updatedBranches
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        branches: [...prev.branches, branch]
      }));
    }
    setEditingBranchIndex(null);
    setNewBranch({
      name: '',
      address: '',
      orderCutoffTime: '',
      deliveryStartTime: '',
      deliveryEndTime: ''
    });
  }, [newBranch, editingBranchIndex, company, formData]);
  const editBranch = useCallback((index: number) => {
    const branch = formData.branches[index];
    setNewBranch({
      name: branch.name,
      address: branch.address,
      orderCutoffTime: branch.orderCutoffTime || '',
      deliveryStartTime: branch.deliveryStartTime || '',
      deliveryEndTime: branch.deliveryEndTime || ''
    });
    setEditingBranchIndex(index);
  }, [formData.branches]);
  const removeBranch = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      branches: prev.branches.filter((_: any, i: number) => i !== index)
    }));
  }, []);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" data-id="element-849">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl border border-gray-200 my-8" data-id="element-850">
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-851">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-852">
            {company ? 'Editar' : 'Nueva'} Empresa
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-853">
            <X size={20} data-id="element-854" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto" data-id="element-855">
          {/* Basic Info */}
          <div data-id="element-856">
            <h3 className="text-sm font-bold text-gray-900 mb-4" data-id="element-857">
              Información Básica
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-858">
              <div data-id="element-859">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-860">
                  Nombre de la Empresa
                </label>
                <input type="text" value={formData.name} onChange={e => handleChange('name', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900" required data-id="element-861" />
              </div>
              <div data-id="element-862">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-863">
                  RNC
                </label>
                <input type="text" value={formData.rnc} onChange={e => handleChange('rnc', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900" required data-id="element-864" />
              </div>
              <div className="md:col-span-2" data-id="element-865">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-866">
                  URL del Logo
                </label>
                <input type="url" value={formData.logo} onChange={e => handleChange('logo', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900" placeholder="https://ejemplo.com/logo.png" data-id="element-867" />
              </div>
              <div data-id="element-868">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-869">
                  Subsidio
                </label>
                <input type="text" value={formData.subsidy} onChange={e => handleChange('subsidy', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900" placeholder="RD$ 200 o 50%" data-id="element-870" />
              </div>
              <div className="flex items-center pt-6" data-id="element-871">
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-872">
                  <input type="checkbox" checked={formData.taxExempt} onChange={e => handleChange('taxExempt', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-873" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-874">
                    Exento de ITBIS
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="border-t border-gray-200 pt-6" data-id="element-875">
            <h3 className="text-sm font-bold text-gray-900 mb-4" data-id="element-876">
              Información de Contacto
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-877">
              <div data-id="element-878">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-879">
                  Nombre del Contacto
                </label>
                <input type="text" value={formData.contactName} onChange={e => handleChange('contactName', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900" placeholder="Juan Pérez" data-id="element-880" />
              </div>
              <div data-id="element-881">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-882">
                  Email
                </label>
                <input type="email" value={formData.contactEmail} onChange={e => handleChange('contactEmail', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900" placeholder="contacto@empresa.com" data-id="element-883" />
              </div>
              <div data-id="element-884">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-885">
                  Teléfono
                </label>
                <input type="tel" value={formData.contactPhone} onChange={e => handleChange('contactPhone', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900" placeholder="809-555-0000" data-id="element-886" />
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="border-t border-gray-200 pt-6" data-id="element-887">
            <h3 className="text-sm font-bold text-gray-900 mb-4" data-id="element-888">
              Horarios Predeterminados
            </h3>
            <div className="grid grid-cols-3 gap-4" data-id="element-889">
              <div data-id="element-890">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-891">
                  Cierre Pedidos
                </label>
                <input type="time" value={formData.orderCutoffTime} onChange={e => handleChange('orderCutoffTime', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900" data-id="element-892" />
              </div>
              <div data-id="element-893">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-894">
                  Inicio Entregas
                </label>
                <input type="time" value={formData.deliveryStartTime} onChange={e => handleChange('deliveryStartTime', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900" data-id="element-895" />
              </div>
              <div data-id="element-896">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-897">
                  Fin Entregas
                </label>
                <input type="time" value={formData.deliveryEndTime} onChange={e => handleChange('deliveryEndTime', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900" data-id="element-898" />
              </div>
            </div>
          </div>

          {/* Branches */}
          <div className="border-t border-gray-200 pt-6" data-id="element-899">
            <h3 className="text-sm font-bold text-gray-900 mb-4" data-id="element-900">Sucursales</h3>
            <div className="bg-gray-50 p-4 rounded-xl mb-4 border border-gray-200" data-id="element-901">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-id="element-902">
                <input type="text" placeholder="Nombre de sucursal" value={newBranch.name} onChange={e => setNewBranch({
                ...newBranch,
                name: e.target.value
              })} className="px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm bg-white text-gray-900" data-id="element-903" />
                <input type="text" placeholder="Dirección" value={newBranch.address} onChange={e => setNewBranch({
                ...newBranch,
                address: e.target.value
              })} className="px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm bg-white text-gray-900" data-id="element-904" />
              </div>
              <Button type="button" size="sm" onClick={addBranch} leftIcon={<Plus size={14} data-id="element-906" />} data-id="element-905">
                {editingBranchIndex !== null ? 'Actualizar Sucursal' : 'Agregar Sucursal'}
              </Button>
            </div>

            <div className="space-y-2" data-id="element-907">
              {formData.branches.map((branch: Branch, index: number) => <div key={branch.id || index} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl" data-id="element-908">
                  <div className="flex-1" data-id="element-909">
                    <p className="font-semibold text-gray-900 text-sm" data-id="element-910">
                      {branch.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-911">
                      {branch.address}
                    </p>
                  </div>
                  <div className="flex gap-2" data-id="element-912">
                    <button type="button" onClick={() => editBranch(index)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-913">
                      <Edit2 size={16} data-id="element-914" />
                    </button>
                    <button type="button" onClick={() => removeBranch(index)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-915">
                      <Trash2 size={16} data-id="element-916" />
                    </button>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200" data-id="element-917">
            <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-918">
              Cancelar
            </Button>
            <Button type="submit" fullWidth data-id="element-919">
              {company ? 'Guardar Cambios' : 'Crear Empresa'}
            </Button>
          </div>
        </form>
      </div>
    </div>;
}
// Company Details Modal
function CompanyDetailsModal({
  company,
  orderCount,
  employeeCount,
  onClose
}: any) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-920">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl border border-gray-200 max-h-[90vh] overflow-y-auto" data-id="element-921">
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-922">
          <div className="flex items-center gap-3" data-id="element-923">
            {company.logo ? <img src={company.logo} alt={company.name} className="w-12 h-12 object-contain rounded-lg bg-gray-50 p-2" data-id="element-924" /> : <div className="p-3 bg-gray-100 rounded-lg" data-id="element-925">
                <Building2 className="w-6 h-6 text-gray-700" data-id="element-926" />
              </div>}
            <h2 className="text-xl font-bold text-gray-900" data-id="element-927">{company.name}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-928">
            <X size={20} data-id="element-929" />
          </button>
        </div>

        <div className="p-6 space-y-6" data-id="element-930">
          <div data-id="element-931">
            <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-932">
              Información General
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-933">
              <div data-id="element-934">
                <p className="text-xs text-gray-500" data-id="element-935">RNC</p>
                <p className="font-mono font-medium text-gray-900" data-id="element-936">
                  {company.rnc}
                </p>
              </div>
              <div data-id="element-937">
                <p className="text-xs text-gray-500" data-id="element-938">Estado Fiscal</p>
                <p className="font-medium text-gray-900" data-id="element-939">
                  {company.taxExempt ? 'Exento ITBIS' : 'ITBIS Aplica'}
                </p>
              </div>
              <div data-id="element-940">
                <p className="text-xs text-gray-500" data-id="element-941">Subsidio</p>
                <p className="font-medium text-gray-900" data-id="element-942">{company.subsidy}</p>
              </div>
              <div data-id="element-943">
                <p className="text-xs text-gray-500" data-id="element-944">Empleados</p>
                <p className="font-medium text-gray-900" data-id="element-945">{employeeCount}</p>
              </div>
            </div>
          </div>

          {(company.contactName || company.contactEmail || company.contactPhone) && <div data-id="element-946">
              <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-947">Contacto</h3>
              <div className="space-y-2" data-id="element-948">
                {company.contactName && <p className="text-sm text-gray-600 flex items-center gap-2" data-id="element-949">
                    <User size={14} data-id="element-950" /> {company.contactName}
                  </p>}
                {company.contactEmail && <p className="text-sm text-gray-600 flex items-center gap-2" data-id="element-951">
                    <Mail size={14} data-id="element-952" /> {company.contactEmail}
                  </p>}
                {company.contactPhone && <p className="text-sm text-gray-600 flex items-center gap-2" data-id="element-953">
                    <Phone size={14} data-id="element-954" /> {company.contactPhone}
                  </p>}
              </div>
            </div>}

          <div data-id="element-955">
            <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-956">Horarios</h3>
            <div className="grid grid-cols-3 gap-4" data-id="element-957">
              <div className="p-3 bg-orange-50 rounded-xl border border-orange-200" data-id="element-958">
                <p className="text-xs font-medium text-orange-700 mb-1" data-id="element-959">
                  📅 Cierre de Pedidos
                </p>
                <p className="text-lg font-bold text-orange-900" data-id="element-960">
                  {company.orderCutoffTime}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200" data-id="element-961">
                <p className="text-xs font-medium text-gray-600 mb-1" data-id="element-962">
                  🚚 Inicio Entregas
                </p>
                <p className="text-lg font-bold text-gray-900" data-id="element-963">
                  {company.deliveryStartTime}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200" data-id="element-964">
                <p className="text-xs font-medium text-gray-600 mb-1" data-id="element-965">
                  ⏰ Fin Entregas
                </p>
                <p className="text-lg font-bold text-gray-900" data-id="element-966">
                  {company.deliveryEndTime}
                </p>
              </div>
            </div>
          </div>

          <div data-id="element-967">
            <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-968">
              Sucursales ({company.branches?.length || 0})
            </h3>
            <div className="space-y-2" data-id="element-969">
              {company.branches?.map((branch: Branch) => <div key={branch.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200" data-id="element-970">
                  <p className="font-semibold text-gray-900" data-id="element-971">{branch.name}</p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-972">{branch.address}</p>
                </div>)}
            </div>
          </div>

          <div data-id="element-973">
            <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-974">
              Estadísticas
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-975">
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200" data-id="element-976">
                <p className="text-xs text-gray-500" data-id="element-977">Total de Pedidos</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-978">{orderCount}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200" data-id="element-979">
                <p className="text-xs text-gray-500" data-id="element-980">Sucursales</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-981">
                  {company.branches?.length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200" data-id="element-982">
          <Button onClick={onClose} fullWidth data-id="element-983">
            Cerrar
          </Button>
        </div>
      </div>
    </div>;
}